// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import EvaluationRule from "./evaluation-rule.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": true
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "identifier": {
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      },
      "name": "identifier",
      "selected_by_default": null,
      "translated": false
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": false,
      "translated": true
    },
    "name_da": {
      "column": null,
      "name": "name_da",
      "selected_by_default": false,
      "translated": false
    },
    "name_en": {
      "column": null,
      "name": "name_en",
      "selected_by_default": false,
      "translated": false
    },
    "name_no": {
      "column": null,
      "name": "name_no",
      "selected_by_default": false,
      "translated": false
    },
    "name_se": {
      "column": null,
      "name": "name_se",
      "selected_by_default": false,
      "translated": false
    }
  },
  "className": "EvaluationGroup",
  "collectionKey": "evaluation_groups",
  "collectionName": "evaluation_groups",
  "i18nKey": "evaluation_group",
  "camelizedLower": "evaluationGroup",
  "name": "EvaluationGroup",
  "nameDasherized": "evaluation-group",
  "pluralName": "evaluation_groups",
  "ransackable_associations": [
    {
      "className": "ActiveRecordAuditable::Audit",
      "collectionName": "audits",
      "foreignKey": "auditable_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "Audit",
      "through": null
    },
    {
      "className": "ActiveRecordAuditable::Audit",
      "collectionName": "audits",
      "foreignKey": "auditable_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "Audit",
      "through": null
    },
    {
      "className": "EvaluationGroup::Translation",
      "collectionName": null,
      "foreignKey": "evaluation_group_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "EvaluationRule",
      "collectionName": "evaluation_rules",
      "foreignKey": "evaluation_group_id",
      "name": "evaluation_rules",
      "macro": "has_many",
      "resource_name": "EvaluationRule",
      "through": null
    },
    {
      "className": "EvaluationGroup::Translation",
      "collectionName": null,
      "foreignKey": "evaluation_group_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "identifier",
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "EvaluationRule",
      "collectionName": "evaluation_rules",
      "foreignKey": "evaluation_group_id",
      "name": "evaluation_rules",
      "macro": "has_many",
      "resource_name": "EvaluationRule",
      "through": null
    }
  ],
  "paramKey": "evaluation_group",
  "primaryKey": "id"
}

/** Frontend model for EvaluationGroup. */
export default class EvaluationGroup extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  identifier() {
    return this.readAttributeUnderscore("identifier")
  }

  /** @returns {boolean} */
  hasIdentifier() {
    const value = this.identifier()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameDa() {
    return this.readAttributeUnderscore("name_da")
  }

  /** @returns {boolean} */
  hasNameDa() {
    const value = this.nameDa()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameEn() {
    return this.readAttributeUnderscore("name_en")
  }

  /** @returns {boolean} */
  hasNameEn() {
    const value = this.nameEn()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameNo() {
    return this.readAttributeUnderscore("name_no")
  }

  /** @returns {boolean} */
  hasNameNo() {
    const value = this.nameNo()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameSe() {
    return this.readAttributeUnderscore("name_se")
  }

  /** @returns {boolean} */
  hasNameSe() {
    const value = this.nameSe()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./evaluation-rule.js").default>} */
  evaluationRules() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["evaluation_group_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "evaluation_rules",
        model: this,
        modelClass: EvaluationRule
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./evaluation-rule.js").default>>} */
  loadEvaluationRules() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["evaluation_group_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "evaluation_rules",
        model: this,
        modelClass: EvaluationRule
      },
      {ransack}
    )
  }
}
